/* Animated gradient background, WebGL.
 *
 * A procedural terrain/topographic-line shader: value noise drives both a
 * shaded ground-plane gradient and a set of contour lines that trace its
 * ripples, with a fake perspective term so the pattern recedes toward a
 * horizon rather than tiling flatly. Recoloured to this store's existing
 * black/silver/gold palette rather than introducing new colours.
 *
 * This is a real per-pixel shader running every frame - meaningfully
 * heavier than anything else animated in this theme, which is the direct
 * tradeoff for an effect that cannot fail to render the way a low-opacity
 * CSS layer could. Two mitigations: paused via IntersectionObserver
 * whenever its section is off-screen, and skipped entirely under
 * prefers-reduced-motion or where WebGL is unavailable, leaving the
 * section's existing static CSS glow as the background instead.
 */
(function () {
  var VERTEX_SRC = [
    'attribute vec4 a_position;',
    'void main() {',
    '  gl_Position = a_position;',
    '}'
  ].join('\n');

  var FRAGMENT_SRC = [
    '#ifdef GL_ES',
    'precision mediump float;',
    '#endif',
    'uniform vec2 iResolution;',
    'uniform float iTime;',
    '',
    'float hash(vec2 p) {',
    '  return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);',
    '}',
    '',
    'float noise(vec2 p) {',
    '  vec2 i = floor(p);',
    '  vec2 f = fract(p);',
    '  vec2 u = f * f * (3.0 - 2.0 * f);',
    '  return mix(mix(hash(i + vec2(0.0, 0.0)), hash(i + vec2(1.0, 0.0)), u.x),',
    '             mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x), u.y);',
    '}',
    '',
    'void main() {',
    '  vec2 uv = (gl_FragCoord.xy * 2.0 - iResolution.xy) / min(iResolution.x, iResolution.y);',
    '',
    '  // Fake ground perspective: depth grows toward the top of the frame,',
    '  // so the noise field below scrolls and scales as if receding.',
    '  float depth = 1.0 / (uv.y + 1.15);',
    '  vec2 gridUv = vec2(uv.x * depth, depth + iTime * 0.15);',
    '',
    '  float n = noise(gridUv * 3.5);',
    '  float ripples = sin(gridUv.y * 18.0 + n * 8.0 + iTime * 0.5);',
    '  float topoLine = smoothstep(0.03, 0.0, abs(ripples));',
    '',
    '  vec3 trueBlack = vec3(0.031, 0.035, 0.047);',  // matches --aph-black exactly
    '  vec3 silver    = vec3(0.55, 0.62, 0.80);',
    '  vec3 gold      = vec3(0.70, 0.58, 0.36);',
    '',
    '  vec3 col = mix(trueBlack, silver, n * 0.6);',
    '  col += topoLine * gold * depth * 0.4;',
    '',
    '  // Horizon fog / edge fade, on top of the vertical mask the CSS already',
    '  // applies to this canvas - keeps the brightest band (near the top,',
    '  // where depth is largest) from reading as a hard-edged plate.',
    '  float fade = smoothstep(0.1, -1.0, uv.y);',
    '  col *= (1.0 - length(uv) * 0.45) * (1.0 - fade);',
    '',
    '  gl_FragColor = vec4(col, 1.0);',
    '}'
  ].join('\n');

  function compile(gl, type, src) {
    var shader = gl.createShader(type);
    gl.shaderSource(shader, src);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      console.error('gradient-bg shader error:', gl.getShaderInfoLog(shader));
      gl.deleteShader(shader);
      return null;
    }
    return shader;
  }

  function setupCanvas(canvas) {
    if (canvas.hasAttribute('data-gradient-ready')) return;
    canvas.setAttribute('data-gradient-ready', '');

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    var gl = canvas.getContext('webgl');
    if (!gl) return; // no WebGL: leave the canvas empty, the CSS glow underneath still shows

    var vs = compile(gl, gl.VERTEX_SHADER, VERTEX_SRC);
    var fs = compile(gl, gl.FRAGMENT_SHADER, FRAGMENT_SRC);
    if (!vs || !fs) return;

    var program = gl.createProgram();
    gl.attachShader(program, vs);
    gl.attachShader(program, fs);
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      console.error('gradient-bg link error:', gl.getProgramInfoLog(program));
      return;
    }
    gl.useProgram(program);

    var buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]), gl.STATIC_DRAW);
    var posLoc = gl.getAttribLocation(program, 'a_position');
    gl.enableVertexAttribArray(posLoc);
    gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 0, 0);

    var iResolution = gl.getUniformLocation(program, 'iResolution');
    var iTime = gl.getUniformLocation(program, 'iTime');

    var startTime = Date.now();
    var raf = null;
    var visible = false;

    // Rendered at a reduced internal resolution and stretched via CSS -
    // this shader is soft/organic by nature, so the softness costs little
    // visual fidelity while meaningfully cutting per-pixel work.
    var SCALE = 0.6;

    function frame() {
      var w = Math.max(1, Math.round(canvas.clientWidth * SCALE));
      var h = Math.max(1, Math.round(canvas.clientHeight * SCALE));
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w;
        canvas.height = h;
      }
      gl.viewport(0, 0, w, h);
      gl.uniform2f(iResolution, w, h);
      gl.uniform1f(iTime, (Date.now() - startTime) / 1000);
      gl.drawArrays(gl.TRIANGLES, 0, 6);
      if (visible) raf = window.requestAnimationFrame(frame);
    }

    function start() {
      if (raf) return;
      visible = true;
      raf = window.requestAnimationFrame(frame);
    }
    function stop() {
      visible = false;
      if (raf) window.cancelAnimationFrame(raf);
      raf = null;
    }

    // Only spend the GPU cost while this section is actually on screen.
    var host = canvas.closest('.aph-real__header') || canvas.parentElement;
    if ('IntersectionObserver' in window && host) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) { e.isIntersecting ? start() : stop(); });
      }, { threshold: 0.01 });
      io.observe(host);
    } else {
      start();
    }
  }

  function init() {
    var canvases = document.querySelectorAll('[data-gradient-bg]');
    for (var i = 0; i < canvases.length; i++) setupCanvas(canvases[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
